(function (root, factory) {
  const api = factory(root);
  root.TimeseekMarketListings = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof globalThis === 'undefined' ? window : globalThis, function (root) {
  'use strict';

  const LIMITS = Object.freeze({ manifest: 512 * 1024, index: 16 * 1024 * 1024, page: 512 * 1024, recent: 128 * 1024 });
  const PAGE_SIZE = 192;
  const RECENT_SIZE = 20;
  const MAX_ROWS = 200000;
  const INDEX_COLUMNS = Object.freeze(['id', 'source', 'brand', 'model', 'reference', 'condition', 'price', 'in_stock', 'created_at', 'page']);
  const DICTIONARY_COLUMNS = Object.freeze(['source', 'brand', 'model', 'reference', 'condition']);
  const PAGE_CACHE_SIZE = 16;
  const CURRENT_BUCKET_MS = 5 * 60 * 1000;
  const RETRY_COOLDOWN_MS = 30 * 1000;
  const CURRENT_CACHE_SIZE = 4;
  const currentRequests = new WeakMap();
  const INDEX_CACHE_BYTES = 32 * 1024 * 1024;
  const INDEX_CACHE_AGE = 7 * 24 * 3600000;
  const BOOTSTRAP_LIMIT = 1024 * 1024;
  const HASH = /^[a-f0-9]{64}$/;
  const SNAPSHOT = /^sha256-[a-f0-9]{64}$/;
  const fail = (reason) => { throw Error(`market_listings_${reason}`); };
  const integer = (value) => Number.isSafeInteger(value) && value >= 0;
  const label = (value, maximum = 500) => typeof value === 'string' && value.length <= maximum && !/[\u0000-\u001f\u007f]/.test(value);
  const optionalLabel = (value) => value === null || label(value);

  // Cache only public, immutable index bytes. Every hit is checked against the
  // current generation's exact size and SHA before it can become filter data.
  function createIndexCache() {
    if (!root.indexedDB || !root.document) return null;
    let opening;
    const bounded = (operation) => new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), 150);
      Promise.resolve().then(operation).then((value) => { clearTimeout(timer); resolve(value); }, () => { clearTimeout(timer); resolve(null); });
    });
    const database = () => opening ||= bounded(() => new Promise((resolve, reject) => {
      const request = root.indexedDB.open('timeseek-market-listings-v1', 1);
      request.onupgradeneeded = () => request.result.createObjectStore('indexes', { keyPath: 'key' });
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(Error('index_cache_blocked'));
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); opening = null; };
        resolve(db);
      };
    }));
    return {
      async read(key) {
        return bounded(async () => {
          const db = await database(); if (!db) return null;
          return new Promise((resolve, reject) => {
            const request = db.transaction('indexes', 'readonly').objectStore('indexes').get(key);
            request.onsuccess = () => resolve(request.result || null); request.onerror = () => reject(request.error);
          });
        });
      },
      async remove(key) {
        return bounded(async () => {
          const db = await database(); if (!db) return;
          db.transaction('indexes', 'readwrite').objectStore('indexes').delete(key);
        });
      },
      async write(entry) {
        return bounded(async () => {
          const db = await database(); if (!db) return;
          return new Promise((resolve, reject) => {
            const transaction = db.transaction('indexes', 'readwrite'), store = transaction.objectStore('indexes');
            const request = store.getAll();
            request.onsuccess = () => {
              const retained = (request.result || []).filter((v) => v.key !== entry.key && Number.isFinite(v.stored_at)
                && v.stored_at <= entry.stored_at && entry.stored_at - v.stored_at < INDEX_CACHE_AGE
                && Number.isSafeInteger(v.byte_size) && v.byte_size > 0 && v.byte_size <= LIMITS.index)
                .sort((a, b) => b.stored_at - a.stored_at).slice(0, 1);
              let bytes = entry.byte_size;
              const keys = new Set([entry.key]);
              for (const value of retained) if (bytes + value.byte_size <= INDEX_CACHE_BYTES) { keys.add(value.key); bytes += value.byte_size; }
              for (const value of request.result || []) if (!keys.has(value.key)) store.delete(value.key);
              store.put(entry);
            };
            transaction.oncomplete = () => resolve(true); transaction.onerror = transaction.onabort = () => reject(transaction.error);
          });
        });
      },
    };
  }
  let persistentIndexCache;

  function validateBootstrap(raw, view, url, now) {
    if (!raw || raw.schema_version !== 'market-listings-bootstrap/v1' || raw.view !== view
      || !['home', 'all'].includes(view) || !HASH.test(raw.manifest_sha256)
      || Object.keys(raw).sort().join('|') !== 'items|manifest|manifest_sha256|schema_version|view') fail('invalid_bootstrap');
    const manifest = validateManifest(raw.manifest, url, now, true);
    const count = Math.min(view === 'home' ? RECENT_SIZE : 24, manifest.in_stock_count);
    const items = checkOrder(itemsEnvelope({ schema_version: 'market-listing-page/v1', snapshot_id: manifest.snapshot_id, items: raw.items },
      manifest, 'market-listing-page/v1', count, view === 'home' ? RECENT_SIZE : 24, now, listing));
    if (items.some((entry) => !entry.in_stock || instant(manifest.expires_at) > instant(entry.last_seen_at) + 48 * 3600000)) fail('invalid_bootstrap');
    return Object.freeze({ ...raw, items, validated_manifest: manifest });
  }

  function readBootstrap(element, view) {
    const node = element?.querySelector?.(`script[data-ts-market-bootstrap="${view}"]`);
    if (!node || typeof node.textContent !== 'string' || new TextEncoder().encode(node.textContent).length > BOOTSTRAP_LIMIT) return null;
    try {
      const raw = JSON.parse(node.textContent);
      const base = element.dataset?.marketListingsManifestUrl || element.dataset?.tsMarketListingsManifestUrl;
      const origin = root.location?.origin || root.window?.location?.origin;
      const url = safeJsonUrl(base, origin);
      if (!url) return null;
      validateBootstrap(raw, view, url, Date.now());
      return raw;
    } catch (_) { return null; }
  }

  function safeJsonUrl(value, origin) {
    try {
      const url = new URL(value, origin);
      if (typeof value !== 'string' || !value || value.length > 2048 || url.protocol !== 'https:' || url.username || url.password || url.hash
          || ![origin, 'https://cdn.shopify.com'].includes(url.origin) || !url.pathname.endsWith('.json')) return null;
      return url.href;
    } catch (_) { return null; }
  }

  function externalUrl(value, optional = false, allowHttp = false) {
    if (optional && (value === null || value === '')) return '';
    try {
      const url = new URL(value);
      if (typeof value !== 'string' || value.length > 4096 || !(url.protocol === 'https:' || allowHttp && url.protocol === 'http:') || url.username || url.password) return null;
      return url.href;
    } catch (_) { return null; }
  }

  function instant(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|\+00:00)$/.test(value)) return NaN;
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === value.slice(0, 10) ? timestamp : NaN;
  }

  function descriptor(raw, baseUrl, kind, { requiredCount = false } = {}) {
    if (!raw || typeof raw !== 'object' || !HASH.test(raw.sha256) || !integer(raw.byte_size) || raw.byte_size === 0 || raw.byte_size > LIMITS[kind]
        || (requiredCount || raw.item_count !== undefined) && !integer(raw.item_count)) fail('invalid_descriptor');
    // A descriptor may identify only an immutable JSON file beside its manifest.
    if (typeof raw.file !== 'string' || raw.file.length > 200 || !/^[a-z0-9][a-z0-9_-]*\.json$/.test(raw.file)
        || !raw.file.includes(raw.sha256)) fail('invalid_file');
    const url = new URL(raw.file, baseUrl);
    if (url.origin !== new URL(baseUrl).origin) fail('invalid_file_origin');
    return Object.freeze({ file: raw.file, sha256: raw.sha256, byte_size: raw.byte_size, item_count: raw.item_count, url: url.href });
  }

  function validateManifest(raw, url, now, allowStale = false) {
    if (raw?.schema_version !== 'market-listings/v1' || !SNAPSHOT.test(raw.snapshot_id) || raw.page_size !== PAGE_SIZE
        || !integer(raw.item_count) || raw.item_count > MAX_ROWS || !integer(raw.in_stock_count) || raw.in_stock_count > raw.item_count
        || !Array.isArray(raw.pages) || raw.pages.length !== Math.ceil(raw.item_count / PAGE_SIZE)) fail('invalid_manifest');
    const generated = instant(raw.generated_at), expires = instant(raw.expires_at);
    if (!Number.isFinite(generated) || !Number.isFinite(expires) || generated > now + 300000 || expires <= generated) fail('invalid_time');
    const pages = raw.pages.map((entry) => descriptor(entry, url, 'page'));
    const index = descriptor(raw.index, url, 'index', { requiredCount: true });
    const recent = descriptor(raw.recent, url, 'recent');
    if (index.item_count !== raw.item_count || recent.item_count !== undefined && recent.item_count !== Math.min(RECENT_SIZE, raw.in_stock_count)) fail('invalid_count');
    const filenames = new Set([index.file, recent.file, ...pages.map((entry) => entry.file)]);
    if (filenames.size !== pages.length + 2) fail('duplicate_file');
    pages.forEach((entry, n) => {
      if (entry.item_count !== undefined && entry.item_count !== Math.min(PAGE_SIZE, raw.item_count - n * PAGE_SIZE)) fail('invalid_count');
    });
    const result = Object.freeze({ schema_version: raw.schema_version, snapshot_id: raw.snapshot_id, generated_at: raw.generated_at,
      expires_at: raw.expires_at, item_count: raw.item_count, in_stock_count: raw.in_stock_count,
      page_size: PAGE_SIZE, pages: Object.freeze(pages), index, recent });
    checkFresh(result, now, allowStale);
    return result;
  }

  function checkFresh(manifest, now, allowStale = false) {
    if (!Number.isFinite(now) || !allowStale && now >= instant(manifest.expires_at)) fail('expired');
  }

  function metadata(raw, now) {
    if (!raw || !label(raw.id) || !raw.id.trim() || !['brand', 'model', 'reference', 'condition'].every((key) => optionalLabel(raw[key]))
        || typeof raw.price !== 'number' || !Number.isFinite(raw.price) || raw.price <= 0 || typeof raw.in_stock !== 'boolean'
        || !integer(raw.created_at) || raw.created_at > Math.floor(now / 1000) + 300) fail('invalid_item');
    if (raw.source !== undefined && (!label(raw.source) || !raw.source.trim())) fail('invalid_source');
    return { id: raw.id, ...(raw.source === undefined ? {} : { source: raw.source }), brand: raw.brand || '', model: raw.model || '', reference: raw.reference || '', condition: raw.condition || '',
      price: raw.price, in_stock: raw.in_stock, created_at: raw.created_at };
  }

  function listing(raw, now) {
    const value = metadata(raw, now);
    const productUrl = externalUrl(raw.product_url, false, true), imageUrl = externalUrl(raw.image_url, true);
    const seen = instant(raw.last_seen_at);
    if (!label(raw.source) || !raw.source.trim() || !optionalLabel(raw.shop_name) || !productUrl || imageUrl === null
        || !Number.isFinite(seen) || seen > now + 300000) fail('invalid_listing');
    return Object.freeze({ ...value, source: raw.source, product_url: productUrl, image_url: imageUrl,
      shop_name: raw.shop_name || '', last_seen_at: raw.last_seen_at });
  }

  function itemsEnvelope(raw, manifest, schema, expectedCount, maximum, now, project) {
    if (raw?.schema_version !== schema || raw.snapshot_id !== manifest.snapshot_id || !Array.isArray(raw.items)
        || raw.items.length > maximum || expectedCount !== undefined && raw.items.length !== expectedCount
        || raw.item_count !== undefined && raw.item_count !== raw.items.length) fail('invalid_envelope');
    const seen = new Set();
    const values = raw.items.map((entry) => {
      const value = project(entry, now);
      if (seen.has(value.id)) fail('duplicate_id');
      seen.add(value.id);
      return value;
    });
    return Object.freeze(values);
  }

  function checkOrder(items) {
    for (let n = 1; n < items.length; n += 1) {
      const left = items[n - 1], right = items[n];
      if (left.in_stock < right.in_stock || left.in_stock === right.in_stock &&
          (left.created_at < right.created_at || left.created_at === right.created_at && left.id > right.id)) fail('invalid_order');
    }
    return items;
  }

  async function decodeIndex(raw, manifest, now) {
    if (raw?.schema_version !== 'market-listing-index/v1' || raw.snapshot_id !== manifest.snapshot_id
        || !Array.isArray(raw.columns) || raw.columns.length !== INDEX_COLUMNS.length || raw.columns.some((column, n) => column !== INDEX_COLUMNS[n])
        || !Array.isArray(raw.rows) || raw.rows.length !== manifest.item_count || raw.rows.length > MAX_ROWS
        || raw.item_count !== undefined && raw.item_count !== raw.rows.length
        || !raw.dictionaries || Object.keys(raw.dictionaries).length !== DICTIONARY_COLUMNS.length) fail('invalid_index');
    for (const column of DICTIONARY_COLUMNS) {
      const values = raw.dictionaries[column];
      if (!Array.isArray(values) || values.length > MAX_ROWS || values.some((entry) => !optionalLabel(entry)) || new Set(values).size !== values.length) fail('invalid_dictionary');
    }
    const seen = new Set(); let stockCount = 0;
    const workClock = () => root.performance?.now?.() ?? Date.now();
    let workStarted = workClock();
    const items = [];
    for (let n = 0; n < raw.rows.length; n += 1) {
      // Yield according to elapsed work, avoiding hundreds of clamped timers
      // while still leaving the browser time to paint and handle input.
      if (n > 0 && n % 256 === 0 && workClock() - workStarted >= 8) {
        await new Promise((resolve) => setTimeout(resolve, 0)); workStarted = workClock();
      }
      const row = raw.rows[n];
      if (!Array.isArray(row) || row.length !== INDEX_COLUMNS.length) fail('invalid_index_row');
      const decoded = {};
      for (let index = 0; index < INDEX_COLUMNS.length; index += 1) {
        const column = INDEX_COLUMNS[index]; const cell = row[index];
        if (DICTIONARY_COLUMNS.includes(column)) {
          if (!integer(cell) || cell >= raw.dictionaries[column].length) fail('invalid_dictionary_reference');
          decoded[column] = raw.dictionaries[column][cell];
        } else decoded[column] = cell;
      }
      const item = metadata(decoded, now);
      if (seen.has(item.id)) fail('duplicate_id');
      if (decoded.page !== Math.floor(n / PAGE_SIZE)) fail('index_mismatch');
      seen.add(item.id); if (item.in_stock) stockCount += 1;
      items.push(Object.freeze({ ...item, page: decoded.page }));
    }
    if (stockCount !== manifest.in_stock_count) fail('index_mismatch');
    return Object.freeze(checkOrder(items));
  }

  async function readResponse(response, maximum, expectedSize) {
    if (!response?.ok) fail('fetch_failed');
    const announced = response.headers?.get?.('content-length');
    if (announced && (!/^\d+$/.test(announced) || Number(announced) > maximum)) fail('response_too_large');
    let bytes;
    if (response.body?.getReader) {
      const reader = response.body.getReader();
      const parts = []; let length = 0;
      try {
        while (true) {
          const part = await reader.read();
          if (part.done) break;
          length += part.value.byteLength;
          if (length > maximum) fail('response_too_large');
          parts.push(part.value);
        }
      } catch (error) { await reader.cancel().catch(() => {}); throw error; }
      bytes = new Uint8Array(length); let offset = 0;
      for (const part of parts) { bytes.set(part, offset); offset += part.byteLength; }
    } else {
      bytes = new TextEncoder().encode(await response.text());
      if (bytes.byteLength > maximum) fail('response_too_large');
    }
    if (expectedSize !== undefined && bytes.byteLength !== expectedSize) fail('size_mismatch');
    return bytes;
  }

  function createClient(manifestUrl, options = {}) {
    const origin = options.origin || root.location?.origin || root.window?.location?.origin;
    const url = safeJsonUrl(manifestUrl, origin);
    const fetchKey = options.fetchImpl || root.fetch;
    const fetchImpl = options.fetchImpl || root.fetch?.bind(root);
    const currentTime = typeof options.now === 'function' ? options.now : () => options.now === undefined ? Date.now() : options.now;
    if (!url || typeof fetchImpl !== 'function') fail('invalid_source');
    if (options.freshness !== undefined && !['strict', 'last-known'].includes(options.freshness)) fail('invalid_freshness');
    const allowStale = options.freshness === 'last-known';
    const isCurrent = new URL(url).pathname.endsWith('/market-listings-current.json');
    const bootstrap = options.bootstrap === undefined ? null : validateBootstrap(options.bootstrap, options.bootstrap?.view, url, currentTime());
    const indexCache = options.indexCache === false ? null : options.indexCache || (persistentIndexCache ||= createIndexCache());
    let manifestPromise = null, recentPromise = null, indexPromise = null, indexLookup = null;
    let manifestValue = bootstrap?.validated_manifest || null, manifestError = null, manifestRetryAt = 0;
    let bootstrapIntegrity = null;
    let recentError = null, recentRetryAt = 0, indexError = null, indexRetryAt = 0;
    let refreshPromise = null, refreshError = null, nextRefreshAt = 0;
    const pageCache = new Map();
    const pagePending = new Map();
    const waiters = []; let activeRequests = 0;
    const enforceFresh = (value) => checkFresh(value, currentTime(), allowStale);
    const scheduleRefresh = (value = manifestValue) => {
      const now = currentTime(), expires = instant(value.expires_at);
      nextRefreshAt = expires <= now ? now + RETRY_COOLDOWN_MS : Math.min(now + CURRENT_BUCKET_MS, expires);
    };

    async function decodeBytes(bytes, entry) {
      if (entry) {
        if (!root.crypto?.subtle) fail('crypto_unavailable');
        const digest = await root.crypto.subtle.digest('SHA-256', bytes);
        const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
        if (hash !== entry.sha256) fail('digest_mismatch');
      }
      try { return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)); }
      catch (_) { fail('invalid_json'); }
    }

    async function verifyBootstrapManifest() {
      if (!bootstrap) return;
      if (!bootstrapIntegrity) bootstrapIntegrity = decodeBytes(new TextEncoder().encode(JSON.stringify(bootstrap.manifest)), { sha256: bootstrap.manifest_sha256 });
      await bootstrapIntegrity;
    }

    async function request(target, kind, entry) {
      const cacheKey = kind === 'index' && entry ? `${target}\n${entry.sha256}` : null;
      if (cacheKey && indexCache) {
        let cached;
        try { cached = await indexCache.read(cacheKey); } catch (_) { /* Private browsing and quota failures do not block data. */ }
        if (cached) {
          try {
            if (cached.key !== cacheKey || cached.sha256 !== entry.sha256 || cached.byte_size !== entry.byte_size
              || !Number.isFinite(cached.stored_at) || cached.stored_at > currentTime() + 300000
              || currentTime() - cached.stored_at >= INDEX_CACHE_AGE || !(cached.bytes instanceof Uint8Array)
              || cached.bytes.byteLength !== entry.byte_size || cached.bytes.byteLength > LIMITS.index) fail('invalid_index_cache');
            return await decodeBytes(cached.bytes, entry);
          } catch (_) { try { void Promise.resolve(indexCache.remove(cacheKey)).catch(() => {}); } catch (_) {} }
        }
      }
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetchImpl(target, { credentials: 'omit', redirect: 'error', signal: controller.signal });
        const bytes = await readResponse(response, LIMITS[kind], entry?.byte_size);
        const value = await decodeBytes(bytes, entry);
        if (cacheKey && indexCache) {
          const cached = { key: cacheKey, sha256: entry.sha256, byte_size: entry.byte_size, stored_at: currentTime(), bytes };
          try { void Promise.resolve(indexCache.write(cached)).catch(() => {}); } catch (_) { /* Best-effort bounded cache. */ }
        }
        return value;
      } finally { clearTimeout(timeout); }
    }

    function currentManifest() {
      const now = currentTime();
      if (!Number.isFinite(now)) return Promise.reject(Error('market_listings_expired'));
      if (!currentRequests.has(fetchKey)) currentRequests.set(fetchKey, new Map());
      const cache = currentRequests.get(fetchKey);
      const bucket = Math.floor(now / CURRENT_BUCKET_MS);
      const key = url + '\n' + bucket;
      // A bucket boundary cannot bypass an in-flight request or a failed
      // transport's cooldown for this same current-manifest endpoint.
      for (const [cachedKey, cached] of cache) {
        if (!cachedKey.startsWith(url + '\n')) continue;
        if (cached.pending) return cached.pending;
        if (cached.error && now < cached.retryAt) return Promise.reject(cached.error);
      }
      let entry = cache.get(key);
      if (entry) {
        cache.delete(key); cache.set(key, entry);
        if (entry.pending) return entry.pending;
        if (entry.error && now < entry.retryAt) return Promise.reject(entry.error);
        if (entry.value && now < entry.until) return Promise.resolve(entry.value);
      } else {
        entry = {}; cache.set(key, entry);
      }
      while (cache.size > CURRENT_CACHE_SIZE) cache.delete(cache.keys().next().value);
      const target = new URL(url);
      target.searchParams.set('v', String(bucket * (CURRENT_BUCKET_MS / 1000)));
      entry.pending = request(target.href, 'manifest')
        .then((raw) => validateManifest(raw, url, currentTime(), true))
        .then((value) => {
          const checkedAt = currentTime();
          entry.value = value; entry.error = null;
          entry.until = instant(value.expires_at) <= checkedAt
            ? checkedAt + RETRY_COOLDOWN_MS : Math.min(checkedAt + CURRENT_BUCKET_MS, instant(value.expires_at));
          return value;
        }).catch((error) => {
          entry.value = null; entry.error = error; entry.retryAt = currentTime() + RETRY_COOLDOWN_MS;
          throw error;
        }).finally(() => { entry.pending = null; });
      return entry.pending;
    }

    async function manifest() {
      await verifyBootstrapManifest();
      if (manifestValue) { enforceFresh(manifestValue); return manifestValue; }
      if (manifestError && currentTime() < manifestRetryAt) throw manifestError;
      if (!manifestPromise) {
        manifestPromise = (isCurrent ? currentManifest() : request(url, 'manifest')
          .then((raw) => validateManifest(raw, url, currentTime(), allowStale)))
          .then((value) => {
            manifestValue = value; manifestError = null; scheduleRefresh();
            enforceFresh(value);
            return value;
          }).catch((error) => {
            manifestError = error; manifestRetryAt = currentTime() + RETRY_COOLDOWN_MS;
            nextRefreshAt = manifestRetryAt;
            throw error;
          }).finally(() => { manifestPromise = null; });
      }
      return manifestPromise;
    }

    function getFreshness() {
      if (!manifestValue) return null;
      return Object.freeze({ snapshot_id: manifestValue.snapshot_id, generated_at: manifestValue.generated_at,
        expires_at: manifestValue.expires_at, stale: currentTime() >= instant(manifestValue.expires_at),
        refresh_failed: Boolean(refreshError) });
    }

    function isRefreshDue() {
      return isCurrent && Boolean(manifestValue || manifestError) && currentTime() >= nextRefreshAt;
    }

    function refresh() {
      if (refreshPromise) return refreshPromise;
      if (refreshError && currentTime() < nextRefreshAt) return Promise.reject(refreshError);
      refreshPromise = Promise.resolve().then(async () => {
        await verifyBootstrapManifest();
        const { bootstrap: ignoredBootstrap, ...refreshOptions } = options;
        const candidate = createClient(url, refreshOptions);
        const next = await candidate.manifest();
        if (manifestValue?.snapshot_id === next.snapshot_id) {
          // A generation cannot keep its identity while changing dates, SHA
          // bindings or counts. Refresh never turns an old observation fresh.
          if (JSON.stringify(manifestValue) !== JSON.stringify(next)) fail('snapshot_changed');
          refreshError = null; scheduleRefresh();
          return client;
        }
        refreshError = null;
        // Consumers may retain this old client if verification of the new
        // generation's cards/index fails. Do not immediately rediscover and
        // retry that same generation in a rollback-render loop.
        scheduleRefresh(manifestValue || next);
        return candidate;
      }).catch((error) => {
        refreshError = error; nextRefreshAt = currentTime() + RETRY_COOLDOWN_MS;
        throw error;
      }).finally(() => { refreshPromise = null; });
      return refreshPromise;
    }

    async function recent() {
      const value = await manifest();
      if (bootstrap?.view === 'home' && !recentPromise) recentPromise = Promise.resolve(bootstrap.items);
      if (!recentPromise || recentError && currentTime() >= recentRetryAt) {
        recentError = null;
        recentPromise = request(value.recent.url, 'recent', value.recent).then((raw) =>
          checkOrder(itemsEnvelope(raw, value, 'market-listing-page/v1', Math.min(RECENT_SIZE, value.in_stock_count), RECENT_SIZE, currentTime(), listing)))
          .catch((error) => { recentError = error; recentRetryAt = currentTime() + RETRY_COOLDOWN_MS; throw error; });
      }
      const items = await recentPromise;
      if (items.some((entry) => !entry.in_stock) || items.length > value.in_stock_count) fail('invalid_recent');
      enforceFresh(value);
      return items;
    }

    async function initial() {
      const value = await manifest();
      if (bootstrap?.view === 'all') return bootstrap.items;
      if (!value.in_stock_count) return Object.freeze([]);
      return Object.freeze((await page(0)).filter((item) => item.in_stock).slice(0, 24));
    }

    async function index() {
      const value = await manifest();
      if (!indexPromise || indexError && currentTime() >= indexRetryAt) {
        indexError = null;
        indexPromise = request(value.index.url, 'index', value.index).then(async (raw) => {
          const items = await decodeIndex(raw, value, currentTime());
          const lookup = new Map();
          let workStarted = root.performance?.now?.() ?? Date.now();
          for (let n = 0; n < items.length; n += 1) {
            const currentWork = root.performance?.now?.() ?? Date.now();
            if (n > 0 && n % 512 === 0 && currentWork - workStarted >= 8) {
              await new Promise((resolve) => setTimeout(resolve, 0)); workStarted = root.performance?.now?.() ?? Date.now();
            }
            const entry = items[n]; lookup.set(entry.id, entry);
          }
          indexLookup = lookup;
          return items;
        }).catch((error) => { indexError = error; indexRetryAt = currentTime() + RETRY_COOLDOWN_MS; throw error; });
      }
      const items = await indexPromise;
      enforceFresh(value);
      return items;
    }

    async function throttled(operation) {
      if (activeRequests >= 2) await new Promise((resolve) => waiters.push(resolve));
      else activeRequests += 1;
      try { return await operation(); }
      finally { const next = waiters.shift(); if (next) next(); else activeRequests -= 1; }
    }

    async function page(number) {
      const value = await manifest();
      if (!integer(number) || number >= value.pages.length) fail('invalid_page');
      if (pageCache.has(number)) {
        const entry = pageCache.get(number);
        pageCache.delete(number); pageCache.set(number, entry);
        if (!entry.error) return entry;
        if (currentTime() < entry.retryAt) throw entry.error;
        pageCache.delete(number);
      }
      if (!pagePending.has(number)) {
        const entry = value.pages[number];
        const pending = throttled(async () => {
          enforceFresh(value);
          const raw = await request(entry.url, 'page', entry);
          const count = Math.min(PAGE_SIZE, value.item_count - number * PAGE_SIZE);
          const items = checkOrder(itemsEnvelope(raw, value, 'market-listing-page/v1', count, PAGE_SIZE, currentTime(), listing));
          if (items.some((entry, n) => entry.in_stock !== (number * PAGE_SIZE + n < value.in_stock_count))) fail('invalid_stock_order');
          enforceFresh(value);
          pageCache.set(number, items);
          while (pageCache.size > PAGE_CACHE_SIZE) pageCache.delete(pageCache.keys().next().value);
          return items;
        });
        pagePending.set(number, pending);
        pending.then(() => pagePending.delete(number), (error) => {
          pagePending.delete(number); pageCache.set(number, { error, retryAt: currentTime() + RETRY_COOLDOWN_MS });
          while (pageCache.size > PAGE_CACHE_SIZE) pageCache.delete(pageCache.keys().next().value);
        });
      }
      const items = await pagePending.get(number);
      enforceFresh(value);
      return items;
    }

    async function details(selected) {
      if (!Array.isArray(selected) || selected.length > 1000) fail('invalid_selection');
      const value = await manifest();
      if (selected.length === 0) return Object.freeze([]);
      await index();
      const indexed = indexLookup;
      const ids = new Set(); const pages = new Set();
      for (const entry of selected) {
        const expected = indexed.get(entry?.id);
        if (!expected || entry.page !== expected.page || ids.has(entry.id)) fail('invalid_selection');
        ids.add(entry.id); pages.add(expected.page);
      }
      const pageNumbers = [...pages];
      const resolved = await Promise.all(pageNumbers.map(page));
      const found = new Map();
      for (let n = 0; n < resolved.length; n += 1) for (const entry of resolved[n]) {
        const expected = indexed.get(entry.id);
        if (!expected || expected.page !== pageNumbers[n] || found.has(entry.id)
            || expected.source !== undefined && expected.source !== entry.source
            || ['brand', 'model', 'reference', 'condition', 'price', 'in_stock', 'created_at'].some((key) => entry[key] !== expected[key])) fail('index_mismatch');
        found.set(entry.id, entry);
      }
      enforceFresh(value);
      return selected.map((entry) => found.get(entry.id) || fail('missing_listing'));
    }

    // A fresh server-rendered generation is already present in the HTML.
    // Avoid downloading its full manifest a second time on every visit. Stale
    // SSR checks current immediately; fresh SSR follows the existing five-minute
    // refresh bound, without extending its actual expiry or observation dates.
    if (bootstrap && currentTime() < instant(manifestValue.expires_at)) scheduleRefresh();
    const client = Object.freeze({ manifest, recent, initial, index, metadata: index, page, details, getFreshness, isRefreshDue, refresh });
    return client;
  }

  return Object.freeze({ createClient, readBootstrap, safeJsonUrl, PAGE_SIZE, LIMITS });
});
